<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-brain me-2"></i>{{ $t('page.mental_health', 'Mental Health Register') }}</h4>
        </div>
        <button v-if="canAccess" class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <!-- Module-level access gate (Sensitive register) -->
      <div v-if="!canAccess" class="hms-card text-center" style="padding:40px; background:linear-gradient(135deg,#fef3c7,#fde68a); border:1px dashed #d97706;">
        <i class="mdi mdi-shield-alert-outline" style="font-size:48px; color:#b45309;"></i>
        <h4 class="mt-2" style="color:#78350f;">Clinical Access Only</h4>
        <p style="color:#92400e;">The Mental Health register contains protected clinical information about real patients. Access is restricted to clinical roles. This access check has been audit-logged.</p>
        <button class="hms-btn hms-btn-neutral" @click="$router.push('/dashboard')">Back to Dashboard</button>
      </div>
      <div v-else>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.active || 0 }}</div><div class="hms-stat-lbl">Active Clients</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.new_30d || 0 }}</div><div class="hms-stat-lbl">New (30d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.high_suicide_risk || 0 }}</div><div class="hms-stat-lbl">High Suicide Risk</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.psychotic_active || 0 }}</div><div class="hms-stat-lbl">Psychotic (active)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.overdue || 0 }}</div><div class="hms-stat-lbl">Visit overdue</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='clients' }"   @click="tab='clients'"><i class="mdi mdi-account-multiple me-1"></i>Active Clients</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }"    @click="tab='detail'" :disabled="!detail.client"><i class="mdi mdi-card-account-details me-1"></i>Client Card</button>
        <button class="hms-tab" :class="{ active: tab==='defaulters' }" @click="tab='defaulters'; loadDefaulters()"><i class="mdi mdi-alert me-1"></i>Defaulters</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>Indicators</button>
      </div>

      <!-- ============ CLIENTS ============ -->
      <div v-if="tab==='clients'">
        <div class="d-flex justify-content-between mb-2 flex-wrap gap-2">
          <div class="d-flex gap-2">
            <input v-model="search" class="hms-input" placeholder="Search by MH # / name / phone" @keyup.enter="loadClients" style="min-width:280px" />
            <select v-model="riskFilter" class="hms-input" @change="loadClients">
              <option value="">All risk levels</option>
              <option value="imminent">Imminent</option>
              <option value="high">High</option>
              <option value="moderate">Moderate</option>
              <option value="low">Low</option>
              <option value="none">None</option>
            </select>
          </div>
          <button class="hms-btn hms-btn-success" @click="openEnrolModal()"><i class="mdi mdi-account-plus"></i> Enrol</button>
        </div>
        <table class="hms-table">
          <thead>
            <tr><th>MH #</th><th>Client</th><th>Diagnosis</th><th>Severity</th><th>Risk</th><th>Next visit</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="c in clients" :key="c.mh_id" :class="rowClass(c)">
              <td><strong>{{ c.mh_number }}</strong></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }} · {{ c.phone || '' }}</div></td>
              <td>{{ c.primary_diagnosis || '—' }}<div class="hms-muted-sm">{{ c.primary_diagnosis_icd }}</div></td>
              <td><span class="hms-pill" :class="severityPill(c.severity)">{{ c.severity }}</span></td>
              <td>
                <span class="hms-pill" :class="riskPill(c.risk_suicide)">Suicide: {{ c.risk_suicide }}</span>
                <span v-if="c.has_self_harm_history == 1" class="hms-tag hms-tag-warn ms-1">SH hx</span>
              </td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">
                {{ c.next_visit_date || '—' }}
                <div v-if="c.days_to_appointment != null" class="hms-muted-sm">
                  {{ c.days_to_appointment >= 0 ? 'in ' + c.days_to_appointment + 'd' : Math.abs(c.days_to_appointment) + 'd late' }}
                </div>
              </td>
              <td><span class="hms-pill" :class="statusPill(c.status)">{{ c.status }}</span></td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DETAIL ============ -->
      <div v-if="tab==='detail' && detail.client">
        <div class="hms-card mb-3" :class="detail.client.risk_suicide === 'high' || detail.client.risk_suicide === 'imminent' ? 'hms-alert-card' : ''">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.client.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.client.mh_number }} · {{ detail.client.patient_no }} · {{ detail.client.sex }} · DOB {{ detail.client.date_of_birth }}</div>
                <div class="mt-1">
                  <strong>Dx:</strong> {{ detail.client.primary_diagnosis || '—' }} <span class="hms-muted-sm">({{ detail.client.primary_diagnosis_icd || '' }})</span>
                  <span class="hms-pill ms-1" :class="severityPill(detail.client.severity)">{{ detail.client.severity }}</span>
                </div>
                <div v-if="detail.client.secondary_diagnoses" class="hms-muted-sm">Secondary: {{ detail.client.secondary_diagnoses }}</div>
              </div>
              <div class="text-end">
                <div><span class="hms-pill" :class="riskPill(detail.client.risk_suicide)">Suicide: {{ detail.client.risk_suicide }}</span></div>
                <div><span class="hms-pill" :class="riskPill(detail.client.risk_harm_others)">Harm to others: {{ detail.client.risk_harm_others }}</span></div>
                <div class="hms-muted-sm mt-1">Functional: {{ detail.client.functional_status }}</div>
                <div class="hms-muted-sm">Next: <strong>{{ detail.client.next_visit_date || '—' }}</strong></div>
              </div>
            </div>
            <div v-if="detail.client.risk_suicide === 'high' || detail.client.risk_suicide === 'imminent'" class="hms-warn-banner mt-2">
              <i class="mdi mdi-alert-octagon"></i> <strong>HIGH SUICIDE RISK</strong> — review safety plan + ensure escalation pathway documented.
            </div>
            <div v-if="detail.client.safety_plan" class="mt-2"><strong>Safety plan:</strong> {{ detail.client.safety_plan }}</div>
            <div v-if="detail.client.current_medications" class="mt-1"><strong>Current meds:</strong> {{ detail.client.current_medications }}</div>
            <div v-if="detail.client.care_plan" class="hms-muted-sm mt-1"><strong>Care plan:</strong> {{ detail.client.care_plan }}</div>
            <div v-if="detail.client.treatment_supporter_name" class="hms-muted-sm">Supporter: {{ detail.client.treatment_supporter_name }} <span v-if="detail.client.treatment_supporter_phone">· {{ detail.client.treatment_supporter_phone }}</span></div>
            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openVisitModal()"><i class="mdi mdi-clipboard-text"></i> Record Visit</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openCrisisVisit()"><i class="mdi mdi-alarm-light"></i> Crisis Visit</button>
              <button class="hms-btn hms-btn-secondary hms-btn-sm" @click="openUpdateClient()"><i class="mdi mdi-pencil"></i> Update Client</button>
            </div>
          </div>
        </div>

        <!-- VISITS TABLE -->
        <div class="hms-card">
          <div class="hms-card-head">Visits ({{ detail.visits.length }})</div>
          <div class="hms-card-body p-0">
            <table v-if="detail.visits.length" class="hms-table">
              <thead>
                <tr><th>Date</th><th>Type</th><th>PHQ-9</th><th>GAD-7</th><th>AUDIT</th><th>Suicide risk</th><th>Therapy</th><th>Disposition</th><th>By</th></tr>
              </thead>
              <tbody>
                <tr v-for="v in detail.visits" :key="v.visit_id" :class="visitRowClass(v)">
                  <td>{{ v.visit_date }}</td>
                  <td><span class="hms-pill" :class="visitTypePill(v.visit_type)">{{ v.visit_type }}</span></td>
                  <td :class="phq9Class(v.phq9_score)">{{ v.phq9_score != null ? v.phq9_score + '/27' : '—' }}</td>
                  <td :class="gad7Class(v.gad7_score)">{{ v.gad7_score != null ? v.gad7_score + '/21' : '—' }}</td>
                  <td>{{ v.audit_score != null ? v.audit_score + '/40' : '—' }}</td>
                  <td><span class="hms-pill" :class="riskPill(v.suicide_risk)">{{ v.suicide_risk }}</span></td>
                  <td>{{ formatTherapy(v.therapy_type) }}<span v-if="v.therapy_duration_min" class="hms-muted-sm"> · {{ v.therapy_duration_min }}m</span></td>
                  <td>{{ formatDisposition(v.disposition) }}</td>
                  <td>{{ v.recorded_by_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-pad text-center hms-muted">No visits yet.</div>
          </div>
        </div>
      </div>

      <!-- ============ DEFAULTERS ============ -->
      <div v-if="tab==='defaulters'">
        <div v-if="!defaulters.length" class="hms-empty"><i class="mdi mdi-check-circle"></i><h5>No defaulters</h5></div>
        <table v-else class="hms-table">
          <thead><tr><th>MH #</th><th>Client</th><th>Risk</th><th>Diagnosis</th><th>Last appt</th><th>Days late</th><th>Phone</th><th>Supporter</th><th></th></tr></thead>
          <tbody>
            <tr v-for="d in defaulters" :key="d.mh_id" :class="d.risk_suicide === 'high' || d.risk_suicide === 'imminent' ? 'row-action' : ''">
              <td><strong>{{ d.mh_number }}</strong></td>
              <td>{{ d.patient_name }}</td>
              <td><span class="hms-pill" :class="riskPill(d.risk_suicide)">{{ d.risk_suicide }}</span></td>
              <td>{{ d.primary_diagnosis }}</td>
              <td>{{ d.next_visit_date }}</td>
              <td><strong :class="d.days_late > 14 ? 'st-bad' : ''">{{ d.days_late }}d</strong></td>
              <td>{{ d.phone || '—' }}</td>
              <td>{{ d.treatment_supporter_name || '—' }} <span class="hms-muted-sm">{{ d.treatment_supporter_phone }}</span></td>
              <td><button class="hms-btn-link" @click="openDetailById(d.mh_id)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ INDICATORS ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-grid-2 mb-3">
          <div class="hms-card">
            <div class="hms-card-head">Service volume</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Active clients</span><strong>{{ indicators.Indicators && indicators.Indicators.active || 0 }}</strong></div>
              <div class="hms-ind-row"><span>New (period)</span><strong>{{ indicators.Indicators && indicators.Indicators.new_period || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Visits (period)</span><strong>{{ indicators.Indicators && indicators.Indicators.visits_period || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Therapy sessions</span><strong>{{ indicators.Indicators && indicators.Indicators.therapy_sessions || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Crisis visits</span><strong class="st-bad">{{ indicators.Indicators && indicators.Indicators.crisis_visits || 0 }}</strong></div>
              <div class="hms-ind-row"><span>High suicide risk (active)</span><strong class="st-bad">{{ indicators.Indicators && indicators.Indicators.high_risk_active || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Average screening scores</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Avg PHQ-9 (depression)</span><strong>{{ formatScore(indicators.Indicators && indicators.Indicators.avg_phq9) }}/27</strong></div>
              <div class="hms-ind-row"><span>Avg GAD-7 (anxiety)</span><strong>{{ formatScore(indicators.Indicators && indicators.Indicators.avg_gad7) }}/21</strong></div>
              <div class="hms-muted-sm mt-3">PHQ-9 ≥10 = clinically significant depression. GAD-7 ≥10 = moderate-to-severe anxiety.</div>
            </div>
          </div>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">Top diagnoses (active clients)</div>
          <div class="hms-card-body p-0">
            <table v-if="indicators.TopDiagnoses && indicators.TopDiagnoses.length" class="hms-table">
              <thead><tr><th>#</th><th>Diagnosis</th><th>Active clients</th></tr></thead>
              <tbody>
                <tr v-for="(d, i) in indicators.TopDiagnoses" :key="i">
                  <td>{{ i + 1 }}</td>
                  <td>{{ d.diagnosis }}</td>
                  <td><strong>{{ d.clients }}</strong></td>
                </tr>
              </tbody>
            </table>
            <div v-else class="hms-muted text-center hms-pad">No data.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== ENROL MODAL ===== -->
    <div v-if="enrolModal" class="hms-modal-bg" @click.self="enrolModal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head"><h5>Enrol Mental Health Client</h5><button class="hms-icon-btn" @click="enrolModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!enrol.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="enrol.patient_search" class="hms-input" placeholder="Name / patient #" @keyup.enter="findEnrolPatient" />
              <button class="hms-btn hms-btn-blue" @click="findEnrolPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip"><i class="mdi mdi-account"></i> {{ enrol.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="enrol.patient_id=null">change</a></div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Enrolment date *</label><input v-model="enrol.enrollment_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Type</label>
              <select v-model="enrol.enrollment_type" class="hms-input">
                <option value="new">New</option><option value="transfer_in">Transfer-in</option>
                <option value="readmission">Readmission</option><option value="self_referral">Self referral</option>
                <option value="court_ordered">Court-ordered</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Referral source</label>
              <select v-model="enrol.referral_source" class="hms-input">
                <option value="OPD">OPD</option><option value="ED">ED</option><option value="inpatient">Inpatient</option>
                <option value="community">Community</option><option value="school">School</option>
                <option value="court">Court</option><option value="police">Police</option>
                <option value="self">Self</option><option value="family">Family</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Diagnosis ICD-11</label><input v-model="enrol.primary_diagnosis_icd" class="hms-input" placeholder="6A70.1" /></div>
            <div class="col-9"><label class="hms-label">Primary diagnosis *</label><input v-model="enrol.primary_diagnosis" class="hms-input" placeholder="e.g. Major depressive disorder, recurrent" /></div>
            <div class="col-12"><label class="hms-label">Secondary diagnoses</label><textarea v-model="enrol.secondary_diagnoses" class="hms-input" rows="2"></textarea></div>
            <div class="col-3"><label class="hms-label">Severity</label>
              <select v-model="enrol.severity" class="hms-input"><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option><option value="psychotic">Psychotic</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Suicide risk</label>
              <select v-model="enrol.risk_suicide" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="imminent">Imminent</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Harm to others</label>
              <select v-model="enrol.risk_harm_others" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="imminent">Imminent</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Self-neglect</label>
              <select v-model="enrol.risk_self_neglect" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option></select>
            </div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.has_substance_use" :true-value="1" :false-value="0" /> Substance use</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.has_self_harm_history" :true-value="1" :false-value="0" /> Self-harm hx</label></div>
            <div class="col-3"><label class="hms-label">Prior suicide attempts</label><input v-model.number="enrol.prior_suicide_attempts" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Functional status</label>
              <select v-model="enrol.functional_status" class="hms-input"><option value="full">Full</option><option value="partial">Partial</option><option value="dependent">Dependent</option><option value="severe_disability">Severe disability</option></select>
            </div>
            <div class="col-12"><label class="hms-label">Safety plan</label><textarea v-model="enrol.safety_plan" class="hms-input" rows="2" placeholder="Warning signs · coping strategies · trusted contacts · crisis hotline · means restriction"></textarea></div>
            <div class="col-12"><label class="hms-label">Care plan</label><textarea v-model="enrol.care_plan" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Current medications</label><input v-model="enrol.current_medications" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Treatment supporter</label><input v-model="enrol.treatment_supporter_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Supporter phone</label><input v-model="enrol.treatment_supporter_phone" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV name</label><input v-model="enrol.chv_name" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">CHV phone</label><input v-model="enrol.chv_phone" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Next visit</label><input v-model="enrol.next_visit_date" type="date" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="enrolModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !enrol.patient_id || !enrol.primary_diagnosis" @click="saveEnrol">{{ saving?'Saving…':'Enrol' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== VISIT MODAL ===== -->
    <div v-if="visitModal" class="hms-modal-bg" @click.self="visitModal=false">
      <div class="hms-modal" style="width:780px">
        <div class="hms-modal-head"><h5>{{ visit.visit_type === 'crisis' ? '⚠ Crisis Visit' : 'Mental Health Visit' }}</h5><button class="hms-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Date *</label><input v-model="visit.visit_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Type</label>
              <select v-model="visit.visit_type" class="hms-input">
                <option value="initiation">Initiation</option><option value="follow_up">Follow-up</option>
                <option value="crisis">Crisis</option><option value="therapy">Therapy</option>
                <option value="medication_review">Med review</option><option value="refill">Refill</option>
                <option value="admission">Admission</option><option value="discharge_planning">Discharge planning</option>
              </select>
            </div>
            <div class="col-2"><label class="hms-label">PHQ-9</label><input v-model.number="visit.phq9_score" type="number" min="0" max="27" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">GAD-7</label><input v-model.number="visit.gad7_score" type="number" min="0" max="21" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">AUDIT</label><input v-model.number="visit.audit_score" type="number" min="0" max="40" class="hms-input" /></div>

            <div class="col-12"><div class="hms-section-label">RISK SNAPSHOT</div></div>
            <div class="col-4"><label class="hms-label">Suicide risk</label>
              <select v-model="visit.suicide_risk" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="imminent">Imminent</option></select>
            </div>
            <div class="col-4"><label class="hms-label">Harm to others</label>
              <select v-model="visit.harm_to_others" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="imminent">Imminent</option></select>
            </div>
            <div class="col-4"><label class="hms-label">MMSE (cognition)</label><input v-model.number="visit.mmse_score" type="number" min="0" max="30" class="hms-input" /></div>
            <div v-if="visit.suicide_risk === 'high' || visit.suicide_risk === 'imminent' || visit.harm_to_others === 'high' || visit.harm_to_others === 'imminent'" class="col-12">
              <label class="hms-label">Safety action taken *</label>
              <textarea v-model="visit.safety_action" class="hms-input" rows="2" placeholder="Means restriction, escalation, admission, contact made with supporter"></textarea>
            </div>

            <div class="col-12"><div class="hms-section-label">MENTAL STATE EXAMINATION</div></div>
            <div class="col-4"><label class="hms-label">Appearance</label><input v-model="visit.mse_appearance" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Behaviour</label><input v-model="visit.mse_behaviour" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Speech</label><input v-model="visit.mse_speech" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Mood</label><input v-model="visit.mse_mood" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Affect</label><input v-model="visit.mse_affect" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Perception</label><input v-model="visit.mse_perception" class="hms-input" placeholder="hallucinations / illusions / none" /></div>
            <div class="col-6"><label class="hms-label">Thought form</label><input v-model="visit.mse_thought_form" class="hms-input" /></div>
            <div class="col-6"><label class="hms-label">Thought content</label><input v-model="visit.mse_thought_content" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Insight</label>
              <select v-model="visit.mse_insight" class="hms-input"><option value="full">Full</option><option value="partial">Partial</option><option value="poor">Poor</option><option value="none">None</option></select>
            </div>
            <div class="col-3"><label class="hms-label">Judgement</label>
              <select v-model="visit.mse_judgement" class="hms-input"><option value="intact">Intact</option><option value="impaired">Impaired</option><option value="severely_impaired">Severely impaired</option></select>
            </div>

            <div class="col-12"><div class="hms-section-label">TREATMENT</div></div>
            <div class="col-12"><label class="hms-label">Medications dispensed</label><input v-model="visit.medications_dispensed" class="hms-input" placeholder="Fluoxetine 20mg OD x30; Risperidone 2mg BD x30…" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="visit.medications_changed" :true-value="1" :false-value="0" /> Meds changed</label></div>
            <div class="col-9"><label class="hms-label">Med change notes</label><input v-model="visit.medication_change_notes" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Therapy</label>
              <select v-model="visit.therapy_type" class="hms-input">
                <option value="none">None</option><option value="CBT">CBT</option><option value="IPT">IPT</option>
                <option value="DBT">DBT</option><option value="family">Family</option><option value="group">Group</option>
                <option value="supportive">Supportive</option><option value="MI">Motivational interviewing</option>
                <option value="psychoeducation">Psychoeducation</option><option value="other">Other</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Duration (min)</label><input v-model.number="visit.therapy_duration_min" type="number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Disposition</label>
              <select v-model="visit.disposition" class="hms-input">
                <option value="continue_outpatient">Continue OP</option><option value="admit">Admit</option>
                <option value="refer_specialist">Refer specialist</option><option value="referral_higher">Refer higher level</option>
                <option value="crisis_stabilise">Crisis stabilise</option><option value="discharge">Discharge</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Assessment</label><textarea v-model="visit.assessment_notes" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Plan</label><textarea v-model="visit.plan_notes" class="hms-input" rows="2"></textarea></div>
            <div class="col-4"><label class="hms-label">Next visit</label><input v-model="visit.next_visit_date" type="date" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="visitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVisit">{{ saving?'Saving…':'Save Visit' }}</button>
        </div>
      </div>
    </div>

    <!-- ===== UPDATE CLIENT MODAL ===== -->
    <div v-if="updateModal" class="hms-modal-bg" @click.self="updateModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Update client</h5><button class="hms-icon-btn" @click="updateModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Status</label>
              <select v-model="upd.status" class="hms-input">
                <option value="active">Active</option><option value="stable">Stable</option>
                <option value="remission">Remission</option><option value="transferred">Transferred</option>
                <option value="discharged">Discharged</option><option value="lost">Lost</option><option value="died">Died</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Severity</label>
              <select v-model="upd.severity" class="hms-input"><option value="mild">Mild</option><option value="moderate">Moderate</option><option value="severe">Severe</option><option value="psychotic">Psychotic</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Suicide risk</label>
              <select v-model="upd.risk_suicide" class="hms-input"><option value="none">None</option><option value="low">Low</option><option value="moderate">Moderate</option><option value="high">High</option><option value="imminent">Imminent</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Next visit</label><input v-model="upd.next_visit_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Safety plan</label><textarea v-model="upd.safety_plan" class="hms-input" rows="3"></textarea></div>
            <div class="col-12"><label class="hms-label">Current medications</label><input v-model="upd.current_medications" class="hms-input" /></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="updateModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveUpdate">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    </div> <!-- /canAccess v-else -->

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "MentalHealth",
  components: { Navigation },
  data() {
    return {
      tab: 'clients', saving: false,
      canAccess: false,
      stats: {},
      clients: [], search: '', riskFilter: '',
      detail: { client: null, visits: [] },
      defaulters: [],
      indicatorPeriod: 'this_month', indicators: {},

      enrolModal: false, enrol: this.blankEnrol(),
      visitModal: false, visit: this.blankVisit(),
      updateModal: false, upd: { status: 'active', severity: 'moderate', risk_suicide: 'none', next_visit_date: '', safety_plan: '', current_medications: '' }
    };
  },
  async mounted() {
    try {
      var r = await executeJson({ function: 'canAccessSensitiveRegister', module: 'mental_health' }, hms);
      this.canAccess = !!(r.data && r.data.success && r.data.data && r.data.data.can);
    } catch (e) { this.canAccess = false; }
    if (this.canAccess) this.loadAll();
  },
  methods: {
    blankEnrol() {
      return {
        patient_id: null, patient_name: '', patient_search: '',
        enrollment_date: new Date().toISOString().substring(0,10), enrollment_type: 'new', referral_source: 'OPD',
        primary_diagnosis_icd: '', primary_diagnosis: '', secondary_diagnoses: '', severity: 'moderate',
        risk_suicide: 'none', risk_harm_others: 'none', risk_self_neglect: 'none',
        has_substance_use: 0, has_self_harm_history: 0, prior_suicide_attempts: 0, safety_plan: '',
        functional_status: 'full', living_situation: '', care_plan: '',
        treatment_supporter_name: '', treatment_supporter_phone: '', chv_name: '', chv_phone: '',
        next_visit_date: '', current_medications: '', notes: ''
      };
    },
    blankVisit() {
      return {
        visit_date: new Date().toISOString().substring(0,10), visit_type: 'follow_up',
        mse_appearance: '', mse_behaviour: '', mse_speech: '', mse_mood: '', mse_affect: '',
        mse_thought_form: '', mse_thought_content: '', mse_perception: '', mse_cognition: '',
        mse_insight: 'full', mse_judgement: 'intact',
        phq9_score: null, gad7_score: null, audit_score: null, mmse_score: null,
        suicide_risk: 'none', harm_to_others: 'none', safety_action: '',
        medications_dispensed: '', medications_changed: 0, medication_change_notes: '',
        therapy_type: 'none', therapy_duration_min: null,
        disposition: 'continue_outpatient', next_visit_date: '', assessment_notes: '', plan_notes: ''
      };
    },

    async loadAll() { await this.loadClients(); },
    async loadClients() {
      try {
        const r = await executeJson({ function: 'getMhClients', search: this.search, risk_suicide: this.riskFilter }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.clients = d.Clients || [];
          this.stats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },

    openEnrolModal() { this.enrol = this.blankEnrol(); this.enrolModal = true; },
    async findEnrolPatient() {
      const q = (this.enrol.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const p = ((r.data.data && (r.data.data.Patients || r.data.data.patients)) || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.enrol.patient_id = p.patient_id;
        this.enrol.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveEnrol() {
      this.saving = true;
      try {
        const params = { function: 'enrolMhClient', ...this.enrol };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.enrolModal = false; await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(c) { await this.openDetailById(c.mh_id); },
    async openDetailById(mhId) {
      try {
        const r = await executeJson({ function: 'getMhClientDetail', mh_id: mhId }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail = { client: d.Client, visits: d.Visits || [] };
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    openVisitModal() { this.visit = this.blankVisit(); this.visitModal = true; },
    openCrisisVisit() { this.visit = this.blankVisit(); this.visit.visit_type = 'crisis'; this.visitModal = true; },
    async saveVisit() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordMhVisit', mh_id: this.detail.client.mh_id, ...this.visit }, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.openDetailById(this.detail.client.mh_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openUpdateClient() {
      const c = this.detail.client;
      this.upd = {
        status: c.status, severity: c.severity, risk_suicide: c.risk_suicide,
        next_visit_date: c.next_visit_date || '', safety_plan: c.safety_plan || '',
        current_medications: c.current_medications || ''
      };
      this.updateModal = true;
    },
    async saveUpdate() {
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateMhClient', mh_id: this.detail.client.mh_id, ...this.upd }, hms);
        if (r.data && r.data.success) { this.updateModal = false; await this.openDetailById(this.detail.client.mh_id); await this.loadClients(); }
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadDefaulters() {
      try {
        const r = await executeJson({ function: 'getMhDefaulters' }, hms);
        if (r.data && r.data.success) this.defaulters = (r.data.data && r.data.data.Defaulters) || [];
      } catch (e) { console.error(e); }
    },
    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getMhIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) this.indicators = r.data.data || {};
      } catch (e) { console.error(e); }
    },

    // ===== formatting =====
    rowClass(c) {
      if (c.risk_suicide === 'imminent') return 'row-action';
      if (c.risk_suicide === 'high') return 'row-warn';
      if (c.days_to_appointment != null && c.days_to_appointment < -7) return 'row-warn';
      return '';
    },
    visitRowClass(v) {
      if (v.visit_type === 'crisis' || v.suicide_risk === 'imminent') return 'row-action';
      if (v.suicide_risk === 'high') return 'row-warn';
      return '';
    },
    severityPill(s) { return ({ mild: 'hms-pill-success', moderate: 'hms-pill-info', severe: 'hms-pill-warn', psychotic: 'hms-pill-danger' })[s] || ''; },
    riskPill(r) { return ({ none: 'hms-pill-success', low: 'hms-pill-info', moderate: 'hms-pill-warn', high: 'hms-pill-danger', imminent: 'hms-pill-danger' })[r] || ''; },
    statusPill(s) { return ({ active: 'hms-pill-success', stable: 'hms-pill-info', remission: 'hms-pill-info', transferred: 'hms-pill-muted', discharged: 'hms-pill-muted', lost: 'hms-pill-warn', died: 'hms-pill-muted' })[s] || ''; },
    visitTypePill(t) { return ({ initiation: 'hms-pill-success', follow_up: 'hms-pill-info', crisis: 'hms-pill-danger', therapy: 'hms-pill-info', medication_review: 'hms-pill-warn', refill: 'hms-pill-muted', admission: 'hms-pill-warn', discharge_planning: 'hms-pill-muted' })[t] || ''; },
    formatTherapy(t) { return t === 'none' ? '—' : t; },
    formatDisposition(d) { return ({ continue_outpatient: 'Continue OP', admit: 'Admit', refer_specialist: 'Refer specialist', referral_higher: 'Refer higher', crisis_stabilise: 'Stabilised', discharge: 'Discharge' })[d] || d; },
    phq9Class(s) { if (s == null) return ''; if (s >= 20) return 'st-bad'; if (s >= 10) return 'st-warn'; return ''; },
    gad7Class(s) { if (s == null) return ''; if (s >= 15) return 'st-bad'; if (s >= 10) return 'st-warn'; return ''; },
    formatScore(n) { return n != null ? Number(n).toFixed(1) : '—'; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 26px; font-weight: 700; color: #1a202c; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-info { color: #2c5282; } .hms-stat-success { color: #276749; } .hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card.hms-alert-card { border-left: 4px solid #e53e3e; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 8px 0 6px; }

.hms-warn-banner { background: #fff5f5; border: 1px solid #fc8181; color: #742a2a; padding: 10px; border-radius: 6px; font-size: 14px; }
.hms-warn-banner .mdi { font-size: 18px; vertical-align: middle; margin-right: 4px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }
.hms-tag { padding: 2px 6px; border-radius: 6px; font-size: 10px; font-weight: 600; }
.hms-tag-warn { background: #feebc8; color: #7b341e; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #38a169; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.st-bad  { color: #c53030; font-weight: 600; }
.st-warn { color: #b45309; font-weight: 600; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
